import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : form;
      const { data } = await api.post(path, payload);
      setUser(data.user);
      navigate("/app", { replace: true });
    } catch (err) {
      setError(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#ffce00] text-black p-12">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center p-1.5 border border-black/10">
            <img src="/tejom-logo.png" alt="Tejom Digital" className="w-full h-full object-contain" />
          </div>
          <span className="font-heading font-extrabold text-lg tracking-tight">TD News Fetch</span>
        </div>
        <div>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-black/60 mb-4">Fetch · Rewrite · Rank</p>
          <h1 className="font-heading font-extrabold text-4xl xl:text-5xl tracking-tighter leading-none mb-6">
            Real-time news,<br />rewritten &amp; SEO-ready.
          </h1>
          <ul className="space-y-3 text-black/80">
            {["Auto-fetch every 15 / 30 minutes", "AI rewrite in 5 Indian languages (800+ words)", "Authenticity + Google E-E-A-T scoring", "Instant copyright infringement flags"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-sm">
                <Check className="w-4 h-4 text-black" aria-hidden="true" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <p className="font-mono text-xs text-black/60">Tejom Digital · 2026</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-11 h-11 border border-border rounded-full flex items-center justify-center p-1">
              <img src="/tejom-logo.png" alt="Tejom Digital" className="w-full h-full object-contain" />
            </div>
            <span className="font-heading font-extrabold text-lg">TD News Fetch</span>
          </div>

          <div className="flex border border-border mb-8">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`tab-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className={`flex-1 py-3 text-sm font-mono uppercase tracking-wider transition-colors ${mode === m ? "bg-[#111110] text-white" : "bg-white text-muted-foreground hover:text-foreground"}`}
              >
                {m === "login" ? "Sign In" : "Register"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="font-mono text-xs uppercase tracking-wider">Name</Label>
                <Input id="name" data-testid="name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 rounded-none" placeholder="Your name" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="font-mono text-xs uppercase tracking-wider">Email</Label>
              <Input id="email" type="email" data-testid="email-input" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 rounded-none" placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="password" className="font-mono text-xs uppercase tracking-wider">Password</Label>
              <Input id="password" type="password" data-testid="password-input" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1 rounded-none" placeholder="••••••••" />
            </div>

            {error && (
              <p data-testid="auth-error" className="text-sm text-destructive font-mono">{error}</p>
            )}

            <Button type="submit" data-testid="submit-auth" disabled={loading}
              className="w-full rounded-none bg-[#111110] hover:bg-[#ffce00] text-white hover:text-black transition-colors py-6 font-mono uppercase tracking-wider">
              {loading ? "Please wait\u2026" : (mode === "login" ? "Sign In" : "Create Account")}
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
            </Button>
          </form>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-border" />
            <span className="font-mono text-xs text-muted-foreground uppercase">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <Button variant="outline" data-testid="google-login" onClick={googleLogin}
            className="w-full rounded-none py-6 border-border font-mono">
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4 mr-2" aria-hidden="true" />
            Continue with Google
          </Button>
        </div>
      </div>
    </div>
  );
}
